import React from 'react';
import { portalContactChannels, PORTAL_CONTACT } from '../lib/portalContact';

// Supplier portal footer – full-bleed band (supersedes the revised Option-C
// layout, which was a centred card: it had margins either side AND kept its
// columns bunched left, so there was dead space on both sides).
//
// The band spans edge to edge; the content inside sits on the same page
// container as everything above it, so it lines up rather than looking
// unmoored. No card treatment – a single top border is separation enough, and
// it reads as a page footer rather than a floating panel.
//
// Three items in EQUAL THIRDS: Help · Email us · Call us. Equal thirds is what
// balances it – the spare width divides between all three rather than pooling
// in one column, so no void appears. Each item is icon + label + value,
// top-aligned, so the label row and the value row run straight across.
//
// The brand block is gone (the header already carries the branding), as are
// "How briefs work" and "How compliance works" (both appear inline beside
// their dashboard section headings – the footer was duplicating them). The
// HELP label stays: on its own it reads slightly redundant, but it's what lets
// all three items share a label row and a value row.
//
// Icons are the GREEN ROUNDED-SQUARE TILE tier – utility actions the user
// takes. Hexagons stay for brand/status/verification. Same job, same
// treatment. See the footer brief §6.
//
// Contact + attribution come from the single portalContact config, never
// hard-coded. Email and phone share ONE treatment – they're equivalent
// actions and shouldn't differ.

const TILE = 'w-11 h-11 rounded-[11px] text-white grid place-items-center flex-shrink-0 shadow-[0_1px_2px_rgba(20,58,38,0.16)]';
// Matches the Help & Contact drawer tiles exactly.
const TILE_BG = { background: 'linear-gradient(135deg, #92D400, #009159)' };
const LABEL = "font-['Metropolis'] text-[9.5px] font-bold tracking-[0.12em] uppercase text-[#9AA189]";
const VALUE = "font-['Metropolis'] text-[14.5px] font-semibold text-ink hover:text-[#009159] hover:underline transition-colors break-words focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159]/40 rounded-sm";

// Icon + label + value, top-aligned so rows line up across the three thirds.
function FooterItem({ icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <span className={TILE} style={TILE_BG} aria-hidden="true">{icon}</span>
      <div className="min-w-0">
        <div className={LABEL}>{label}</div>
        <div className="mt-[3px]">{children}</div>
      </div>
    </div>
  );
}

const glyph = (paths) => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);

// Each portal page sets its own content width (dashboard 1140, compliance
// max-w-5xl, response 920). The band is always full-bleed; its CONTENT takes
// the host page's container so the footer lines up with the content above it
// instead of looking unmoored. Default is the dashboard's.
const DEFAULT_CONTAINER = 'max-w-[1140px] px-6 sm:px-8';

export default function SupplierFooter({ onBrowseHelp, containerClassName = DEFAULT_CONTAINER }) {
  const channels = portalContactChannels();
  const email = channels.find((c) => c.type === 'email');
  const phone = channels.find((c) => c.type === 'phone');

  return (
    <footer className="mt-12 bg-white border-t border-[#D4D9C6]">
      {/* Band is full-bleed; content aligns to the page container. */}
      <div className={`${containerClassName} mx-auto py-6`}>
        {/* Equal thirds above 820px, stacked in order below it. */}
        <div className="grid grid-cols-1 min-[820px]:grid-cols-3 gap-6 min-[820px]:gap-8 items-start">

          <FooterItem
            label="Help"
            icon={glyph(<path d="M9.2 9.3a3 3 0 1 1 4 2.8c-.7.3-1.1.9-1.1 1.6v.6M12 18h.02" />)}>
            <button type="button" onClick={onBrowseHelp}
              className={`${VALUE} text-[#009159] text-left`}>
              Browse help topics →
            </button>
          </FooterItem>

          {email && (
            <FooterItem
              label="Email us"
              icon={glyph(<><rect x="3" y="5.5" width="18" height="13" rx="2" /><path d="M3.6 6.8L12 13l8.4-6.2" /></>)}>
              <a href={`mailto:${email.value}`} className={VALUE}>{email.value}</a>
            </FooterItem>
          )}

          {phone && (
            <FooterItem
              label="Call us"
              icon={glyph(<path d="M5 4.5h4l2 5-2.5 1.5a11 11 0 0 0 4.5 4.5L14.5 13l5 2v4a1.5 1.5 0 0 1-1.6 1.5A15.5 15.5 0 0 1 3.5 6.1 1.5 1.5 0 0 1 5 4.5z" />)}>
              <a href={`tel:${phone.value.replace(/\s+/g, '')}`} className={VALUE}>{phone.value}</a>
              {/* Hours are a caption on the number, not a separate item. */}
              <div className="font-['Metropolis'] text-[12px] text-[#9AA189] leading-tight mt-[3px]">
                {PORTAL_CONTACT.partnerSupport.hours}
              </div>
            </FooterItem>
          )}

        </div>

        {/* Divider + bottom bar – copyright + attribution from config. */}
        <div className="mt-6 pt-4 border-t border-[#EEF1E6] flex items-center justify-between gap-4 flex-wrap">
          <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#7C7F6F]">
            {PORTAL_CONTACT.footer.copyright}
          </span>
          {PORTAL_CONTACT.footer.attribution && (
            <span className="font-['Metropolis'] text-[11px] tracking-[0.06em] text-[#9AA189]">
              Powered by <span className="font-bold tracking-[0.14em] text-[#7C7F6F]">{PORTAL_CONTACT.footer.attribution}</span>
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
